import {
  createBrowserAnalytics,
  canonicalPageUrl,
  webAttribution,
  type EventProperties,
} from "@etlyn/analytics/first-party";

const applicationKey = import.meta.env.VITE_ANALYTICS_APPLICATION_KEY;
export const analyticsConfigured =
  typeof applicationKey === "string" &&
  /^[A-Za-z0-9_-]{16,128}$/.test(applicationKey);
export const preferenceKey = "etlyn.analytics.preference.v3";
export const privacySignalActive = () =>
  navigator.doNotTrack === "1" ||
  navigator.doNotTrack === "yes" ||
  Boolean(
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl,
  );
export function readAnalyticsEnabled() {
  try {
    const preference = localStorage.getItem(preferenceKey);
    if (preference) return preference === "enabled";
    // Preserve previous refusals when moving away from the consent banner.
    return !["etlyn.analytics.consent.v1", "etlyn.analytics.consent.v2"].some(
      (key) => localStorage.getItem(key) === "denied",
    );
  } catch {
    return true;
  }
}
const analytics = analyticsConfigured
  ? createBrowserAnalytics({
      applicationKey,
      apiBaseUrl:
        import.meta.env.VITE_ANALYTICS_API_URL || "https://api.etlyn.com",
      appVersion: "1.2.0",
      collectionMode: "cookieless",
      allowedEvents: {
        newsletter_subscribe: ["screen", "page_url", "source", "medium", "campaign", "referrer_host"],
        page_view: [
          "screen",
          "page_url",
          "source",
          "medium",
          "campaign",
          "referrer_host",
        ],
        click: [
          "screen",
          "target",
          "page_url",
          "source",
          "medium",
          "campaign",
          "referrer_host",
        ],
      },
    })
  : null;
let enabled = readAnalyticsEnabled();
let currentScreen: string | null = null;
let currentUrl: string | undefined;
let attribution: EventProperties | null = null;
let attributionActiveAt = 0;
const landingUrl = window.location.href;
const landingReferrer = document.referrer;
function pageProperties(): EventProperties {
  const now = Date.now();
  const incoming = new URL(window.location.href);
  if (
    !attribution ||
    now - attributionActiveAt >= 30 * 60 * 1000 ||
    incoming.searchParams.has("utm_source")
  ) {
    attribution = webAttribution(
      attribution ? incoming.href : landingUrl,
      attribution ? "" : landingReferrer,
    );
  }
  attributionActiveAt = now;
  return {
    screen: currentScreen!,
    ...(currentUrl ? { page_url: currentUrl } : {}),
    ...attribution,
  };
}
const allowed = () => enabled && !privacySignalActive();
export function recordPage(pathname: string) {
  const screens: Record<string, string> = {
    "/": "home",
    "/home": "home",
    "/about": "about",
    "/services": "services",
    "/blogs": "blog_index",
    "/privacy": "privacy",
  };
  const screen =
    screens[pathname] || (pathname.startsWith("/blogs/") ? "blog_post" : null);
  const url = screen ? canonicalPageUrl(window.location.href) : undefined;
  if (currentScreen === screen && currentUrl === url) return;
  currentScreen = screen;
  currentUrl = url;
  if (screen && allowed())
    void analytics
      ?.track("page_view", pageProperties())
      .then(() => analytics.flush());
}
export function recordNewsletterSubscription() {
  if (allowed() && currentScreen === "blog_index")
    void analytics?.track("newsletter_subscribe", pageProperties()).then(() => analytics.flush());
}
export function setAnalyticsEnabled(value: boolean) {
  const changed = enabled !== value;
  enabled = value;
  if (!value) attribution = null;
  try {
    localStorage.setItem(preferenceKey, value ? "enabled" : "disabled");
  } catch {}
  window.dispatchEvent(new Event("etlyn-analytics-preference"));
  void analytics?.setEnabled(allowed()).then(async () => {
    if (changed && allowed() && currentScreen) {
      await analytics.track("page_view", pageProperties());
      await analytics.flush();
    }
  });
}
export function startAnalytics() {
  if (!analytics) return () => {};
  // Remove identifiers and attribution left by the previous persistent SDK.
  try {
    localStorage.removeItem(`etlyn.analytics.v1.${applicationKey}`);
    sessionStorage.removeItem("etlyn.analytics.attribution.v2");
  } catch {}
  enabled = readAnalyticsEnabled();
  void analytics.setEnabled(allowed());
  const flush = () => {
    void analytics.flush();
  };
  const clicked = (event: MouseEvent) => {
    if (!allowed() || !currentScreen || !(event.target instanceof Element))
      return;
    const target = event.target.closest<HTMLElement>("[data-analytics-target]")
      ?.dataset.analyticsTarget;
    if (
      !target ||
      ![
        "nav_home",
        "nav_services",
        "nav_about",
        "nav_blog",
        "contact",
        "sign_in",
      ].includes(target)
    )
      return;
    void analytics.track("click", { ...pageProperties(), target }).then(flush);
  };
  const synced = (event: StorageEvent) => {
    if (event.key === preferenceKey || event.key === null) {
      enabled = readAnalyticsEnabled();
      if (!enabled) attribution = null;
      void analytics.setEnabled(allowed());
    }
  };
  const interval = window.setInterval(flush, 15000);
  document.addEventListener("click", clicked, true);
  document.addEventListener("visibilitychange", flush);
  window.addEventListener("online", flush);
  window.addEventListener("pagehide", flush);
  window.addEventListener("storage", synced);
  return () => {
    clearInterval(interval);
    document.removeEventListener("click", clicked, true);
    document.removeEventListener("visibilitychange", flush);
    window.removeEventListener("online", flush);
    window.removeEventListener("pagehide", flush);
    window.removeEventListener("storage", synced);
  };
}
